import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { BoardFieldDefinition, Person } from "../../Boards/types"

export type FieldValue = string | number | boolean | string[] | null

const NONE = "__none__"

interface CustomFieldProps {
  field: BoardFieldDefinition
  value: FieldValue
  people: Person[]
  disabled: boolean
  onChange: (value: FieldValue) => void
}

export function CustomField({ field, value, people, disabled, onChange }: CustomFieldProps) {
  const id = `field_${field.id}`
  const label = <Label htmlFor={id}>{field.name}</Label>

  if (field.field_type === "checkbox") {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Checkbox
          id={id}
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <Label htmlFor={id}>{field.name}</Label>
      </div>
    )
  }

  if (field.field_type === "select" || field.field_type === "person") {
    const choices =
      field.field_type === "person"
        ? people.map((person) => ({ value: String(person.id), label: person.name }))
        : field.options.map((option) => ({ value: option, label: option }))

    const current = value === null || value === "" ? NONE : String(value)

    return (
      <div className="space-y-2">
        {label}
        <Select
          value={current}
          disabled={disabled}
          onValueChange={(next) => {
            if (next === NONE) return onChange(null)
            onChange(field.field_type === "person" ? Number(next) : next)
          }}
        >
          <SelectTrigger id={id} className="w-full">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Not set</SelectItem>
            {choices.map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  if (field.field_type === "multi_select") {
    const chosen = Array.isArray(value) ? value : []

    return (
      <div className="space-y-2">
        {label}
        <div className="space-y-1.5">
          {field.options.map((option, index) => {
            const optionId = `${id}_${index}`

            return (
              <div key={option} className="flex items-center gap-2 text-sm">
                <Checkbox
                  id={optionId}
                  checked={chosen.includes(option)}
                  disabled={disabled}
                  onCheckedChange={(checked) =>
                    onChange(checked === true ? [...chosen, option] : chosen.filter((entry) => entry !== option))
                  }
                />
                <Label htmlFor={optionId}>{option}</Label>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const type = field.field_type === "number" ? "number" : field.field_type === "date" ? "date" : "text"

  return (
    <div className="space-y-2">
      {label}
      <Input
        id={id}
        type={type}
        className="w-full"
        readOnly={disabled}
        defaultValue={value === null ? "" : String(value)}
        onBlur={(event) => {
          const raw = event.target.value
          if (raw === "") return onChange(null)
          onChange(field.field_type === "number" ? Number(raw) : raw)
        }}
      />
    </div>
  )
}
